import React from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  LinearProgress,
  Grid,
} from "@mui/material";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import ReactApexChart from "react-apexcharts";
import styles from "./criticalOtDetail.module.scss";
import {
  hero,
  stats,
  technicalSpecs,
  activeProtocols,
  openPorts,
  riskAssessment,
  threatIndicators,
  aiRecommendations,
  protocolTrafficSeries,
  protocolTrafficCategories,
  vulnerabilities,
  commandHistory,
  accessEvents,
  riskTrend,
  riskTrendCategories,
  complianceStatus,
  networkConnections,
} from "./criticalOtData";
import ComplianceBreadcrumb from "../compliance/ComplianceBreadcrumb";

const StatCard = ({ stat }) => {
  const iconMap = {
    risk: <ShieldOutlinedIcon />,
    shield: <ShieldOutlinedIcon />,
    alert: <WarningAmberOutlinedIcon />,
    clock: <AccessTimeOutlinedIcon />,
  };

  return (
    <Card className={styles.statCard}>
      <div className={styles.statHeader}>
        <div className={styles.statIcon}>{iconMap[stat.icon]}</div>
        <div className={styles.statLabel}>{stat.label}</div>
      </div>
      <div className={styles.statValueRow}>
        <div className={styles.statValue}>
          {stat.value}
          {stat.suffix
            ? ` ${stat.suffix}`
            : stat.key === "compliance"
            ? "%"
            : stat.key === "anomaly"
            ? "%"
            : ""}
        </div>
        {stat.change !== null && (
          <div
            className={`${styles.statChange} ${
              stat.change < 0 ? styles.changeDown : styles.changeUp
            }`}
          >
            {stat.change < 0 ? "" : "+"}
            {Math.abs(stat.change)}% vs 30d
          </div>
        )}
      </div>
    </Card>
  );
};

const SpecPill = ({ label, value, tone, hint }) => (
  <div className={`${styles.specPill} ${tone ? styles[`spec${tone}`] : ""}`}>
    <span className={styles.specLabel}>{label}</span>
    <span className={styles.specValue}>{value}</span>
    {hint ? <span className={styles.specHint}>{hint}</span> : null}
  </div>
);

const VulnerabilityRow = ({ vuln }) => (
  <TableRow>
    <TableCell className={styles.vulnName}>
      <div className={styles.linkish}>{vuln.cve}</div>
      <div className={styles.vulnTitle}>{vuln.title}</div>
    </TableCell>
    <TableCell>
      <span
        className={`${styles.severity} ${styles[vuln.severity.toLowerCase()]}`}
      >
        {vuln.severity}
      </span>
    </TableCell>
    <TableCell>{vuln.cvss}</TableCell>
    <TableCell>
      <span
        className={`${styles.exploit} ${styles[vuln.exploit.toLowerCase()]}`}
      >
        {vuln.exploit}
      </span>
    </TableCell>
    <TableCell>{vuln.status}</TableCell>
    <TableCell>{vuln.remediation}</TableCell>
  </TableRow>
);

const StatusBadge = ({ status }) => {
  const tone =
    status === "Normal"
      ? styles.statusNormal
      : status === "Warning"
      ? styles.statusWarning
      : status === "Critical"
      ? styles.statusCritical
      : styles.statusSuspicious;
  return <span className={`${styles.statusBadge} ${tone}`}>{status}</span>;
};

const CriticalOtDetail = () => {
  const protocolOptions = {
    chart: {
      toolbar: { show: false },
      sparkline: { enabled: false },
      zoom: { enabled: false },
    },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 3 },
    colors: ["#0284c7", "#22c55e", "#8b5cf6", "#ef4444"],
    legend: {
      position: "top",
      horizontalAlign: "center",
      labels: { colors: "#475569" },
    },
    xaxis: {
      categories: protocolTrafficCategories,
      labels: { style: { colors: "#475569" } },
    },
    yaxis: { labels: { style: { colors: "#475569" } } },
    grid: { borderColor: "#e5e7eb" },
    markers: { size: 4 },
  };

  const riskTrendOptions = {
    chart: {
      toolbar: { show: false },
      sparkline: { enabled: false },
      zoom: { enabled: false },
    },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 3 },
    colors: ["#ef4444"],
    xaxis: {
      categories: riskTrendCategories,
      labels: { style: { colors: "#475569" } },
    },
    yaxis: { labels: { style: { colors: "#475569" } }, min: 0, max: 100 },
    grid: { borderColor: "#e5e7eb" },
    markers: { size: 4 },
  };

  return (
    <Box className={styles.page}>
      <div className={styles.topBar}>
        <ComplianceBreadcrumb
          items={[
            { label: "CISO Dashboard", href: "/" },
            { label: "Critical OT Assets", href: "/" },
            { label: hero.name },
          ]}
        />
        <Button
          variant="contained"
          startIcon={<CloudDownloadOutlinedIcon />}
          className={styles.exportButton}
        >
          Export Report
        </Button>
      </div>

      <Card className={styles.heroCard}>
        <div className={styles.heroLeft}>
          <div className={styles.iconBubble}>
            <ShieldOutlinedIcon />
          </div>
          <div className={styles.heroInfo}>
            <div className={styles.heroTitleRow}>
              <div className={styles.heroName}>{hero.name}</div>
              <Chip
                label={hero.badge}
                size="small"
                className={styles.heroBadge}
              />
            </div>
            <div className={styles.heroSubtitle}>
              {hero.type} • {hero.model}
            </div>
          </div>
        </div>
        <div className={styles.heroScore}>
          <div className={styles.scoreLabel}>Overall Risk Score</div>
          <div className={styles.scoreValue}>78%</div>
        </div>
      </Card>

      <div className={styles.statGrid}>
        {stats.map((s) => (
          <StatCard key={s.key} stat={s} />
        ))}
      </div>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Card className={styles.specCard} sx={{ mb: 2 }}>
            <Typography className={styles.sectionTitle} fontWeight={700}>
              Technical Specifications
            </Typography>
            <div className={styles.specGrid}>
              {technicalSpecs.map((spec) => (
                <SpecPill key={spec.label} {...spec} />
              ))}
            </div>
            <div className={styles.specFooter}>
              <div className={styles.specFooterGroup}>
                <span className={styles.metaLabel}>Active Protocols</span>
                <div className={styles.tagRow}>
                  {activeProtocols.map((p) => (
                    <span key={p} className={styles.tag}>
                      {p}
                    </span>
                  ))}
                </div>
              </div>
              <div className={styles.specFooterGroup}>
                <span className={styles.metaLabel}>Open Ports</span>
                <div className={styles.tagRow}>
                  {openPorts.map((p) => (
                    <span key={p} className={styles.tag}>
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Card>

          <Card className={styles.chartCard}>
            <Typography className={styles.sectionTitle} fontWeight={700}>
              Protocol Traffic Analysis
            </Typography>
            <ReactApexChart
              options={protocolOptions}
              series={protocolTrafficSeries}
              type="area"
              height={320}
            />
          </Card>

          <Card className={styles.tableCard} sx={{ my: 2 }}>
            <div className={styles.tableHeader}>
              <Typography className={styles.sectionTitle} fontWeight={700}>
                Detected Vulnerabilities
              </Typography>
              <Button
                variant="outlined"
                size="small"
                className={styles.outlineBtn}
              >
                Export CVE Report
              </Button>
            </div>
            <TableContainer className={styles.tableContainer}>
              <Table size="medium">
                <TableHead>
                  <TableRow>
                    <TableCell className={styles.tableHead}>
                      Vulnerability
                    </TableCell>
                    <TableCell className={styles.tableHead}>Severity</TableCell>
                    <TableCell className={styles.tableHead}>CVSS</TableCell>
                    <TableCell className={styles.tableHead}>Exploit</TableCell>
                    <TableCell className={styles.tableHead}>Status</TableCell>
                    <TableCell className={styles.tableHead}>
                      Remediation
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vulnerabilities.map((vuln) => (
                    <VulnerabilityRow key={vuln.cve} vuln={vuln} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          <Card className={styles.tableCard}>
            <Typography className={styles.sectionTitle} fontWeight={700}>
              Real-time Command History
            </Typography>
            <TableContainer className={styles.tableContainer}>
              <Table size="medium">
                <TableHead>
                  <TableRow>
                    <TableCell className={styles.tableHead}>Time</TableCell>
                    <TableCell className={styles.tableHead}>Command</TableCell>
                    <TableCell className={styles.tableHead}>Source</TableCell>
                    <TableCell className={styles.tableHead}>Protocol</TableCell>
                    <TableCell className={styles.tableHead}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {commandHistory.map((row, idx) => (
                    <TableRow key={`${row.command}-${idx}`}>
                      <TableCell>{row.time}</TableCell>
                      <TableCell>{row.command}</TableCell>
                      <TableCell>{row.source}</TableCell>
                      <TableCell>{row.proto}</TableCell>
                      <TableCell>
                        <StatusBadge status={row.status} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card className={styles.aiSideCard}>
            <Typography className={styles.sectionTitle} fontWeight={700}>
              AI Security Analysis
            </Typography>
            <div className={styles.aiBox}>
              <div className={styles.riskTitle}>Risk Assessment</div>
              <ul className={styles.riskList}>
                {riskAssessment.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>

            <div className={styles.riskIndicators}>
              <Typography className={styles.sectionTitle} fontWeight={700}>
                Active Threat Indicators
              </Typography>
              <ul className={styles.bulletList}>
                {threatIndicators.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>

            <div className={styles.recoBox}>
              <Typography className={styles.sectionTitle} fontWeight={700}>
                AI Recommendations
              </Typography>
              <ol className={styles.recoList}>
                {aiRecommendations.map((rec) => (
                  <li key={rec}>{rec}</li>
                ))}
              </ol>
              <div className={styles.recoActions}>
                <Button
                  variant="contained"
                  color="error"
                  className={styles.isolateBtn}
                >
                  Isolate Asset
                </Button>
                <Button variant="contained" className={styles.primaryBtn}>
                  Force Firmware Update
                </Button>
                <Button variant="outlined" className={styles.outlineBtn}>
                  Enable Auth Enforcement
                </Button>
              </div>
            </div>
          </Card>
          <Card className={styles.tableCard} sx={{ my: 2 }}>
            <Typography className={styles.sectionTitle} fontWeight={700}>
              Compliance Status
            </Typography>
            <div className={styles.progressList}>
              {complianceStatus.map((item) => (
                <div key={item.label} className={styles.progressItem}>
                  <span>{item.label}</span>
                  <LinearProgress
                    variant="determinate"
                    value={item.value}
                    className={styles.progressBar}
                    sx={{
                      "& .MuiLinearProgress-bar": {
                        background: item.color,
                      },
                    }}
                  />
                  <span className={styles.progressValue}>{item.value}%</span>
                </div>
              ))}
            </div>
          </Card>

          <Card className={styles.trendSmallCard}>
            <Typography className={styles.sectionTitle} fontWeight={700}>
              Risk Score Trend
            </Typography>
            <ReactApexChart
              options={riskTrendOptions}
              series={[{ name: "Risk score", data: riskTrend }]}
              type="area"
              height={200}
            />
          </Card>
        </Grid>
      </Grid>

      <Card className={styles.tableCard}>
        <Typography className={styles.sectionTitle} fontWeight={700}>
          Active Network Connections
        </Typography>
        <TableContainer className={styles.tableContainer}>
          <Table size="medium">
            <TableHead>
              <TableRow>
                <TableCell className={styles.tableHead}>Source IP</TableCell>
                <TableCell className={styles.tableHead}>Destination</TableCell>
                <TableCell className={styles.tableHead}>Protocol</TableCell>
                <TableCell className={styles.tableHead}>Port</TableCell>
                <TableCell className={styles.tableHead}>Packets</TableCell>
                <TableCell className={styles.tableHead}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {networkConnections.map((row, idx) => (
                <TableRow key={`${row.src}-${idx}`}>
                  <TableCell>{row.src}</TableCell>
                  <TableCell>{row.dest}</TableCell>
                  <TableCell>{row.proto}</TableCell>
                  <TableCell>{row.port}</TableCell>
                  <TableCell>{row.packets}</TableCell>
                  <TableCell>
                    <StatusBadge status={row.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
};

export default CriticalOtDetail;
